import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchGalleryBySlug } from '../cms';
import LightboxModal from '../components/LightboxModal';

export default function SteelRoofPage() {
  const [galleryImages, setGalleryImages] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImageIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    fetchGalleryBySlug('staaltag').then(imgs => setGalleryImages(imgs));
  }, []);

  return (
    <div className="bg-[#ffffff]">
      <Helmet>
        <title>Ståltag i Horsens, Vejle og Hedensted · Få et tilbud - T.H Byg</title>
        <meta 
          name="description" 
          content="Skal du have lagt nyt ståltag nær Horsens og Vejle? TH Byg v/Jacob Hartvig Hansen har stor erfaring med lægning af ståltage. Kontakt os i dag" 
        />
        <meta property="og:title" content="Ståltag i Horsens, Vejle og Hedensted · Få et tilbud - T.H Byg" />
        <meta property="og:description" content="Skal du have lagt nyt ståltag nær Horsens og Vejle? TH Byg v/Jacob Hartvig Hansen har stor erfaring med lægning af ståltage. Kontakt os i dag" />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/4aae8b48-1e4d-42c2-9336-5940e0b091a6/th-byg-34-scaled.jpg" />
      </Helmet>

      {/* Hero Header */}
      <section className="relative bg-[#11161a] text-white py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/4aae8b48-1e4d-42c2-9336-5940e0b091a6/th-byg-34-scaled.jpg"
            alt="Ståltage i Vejle, Horsens og Hedensted"
            className="h-full w-full object-cover opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#11161a] via-[#11161a]/90 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#d92323] px-3.5 py-1 text-xs font-black uppercase text-white mb-6">
              ★ T.H Byg Hovedspeciale
            </div>
            
            <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight leading-tight">
              Ståltag monteret med fokus på kvalitet og finish
            </h1>

            <p className="mt-4 text-xl font-bold text-amber-300">
              Ståltag i Vejle, Horsens, Hedensted og Skanderborg
            </p>

            <p className="mt-6 text-slate-300 text-base sm:text-lg leading-relaxed">
              Hos T-H Byg har vi stor erfaring med lægning af holdbare og smukke ståltage på boliger, sommerhuse og virksomheder. Få lavet et økonomisk fornuftigt tag af enten Decra eller Metrotile.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#d92323] px-8 py-4 text-base font-extrabold text-white shadow-xl hover:bg-[#b81b1b] transition-all"
              >
                <span>Få et tilbud på ståltag</span>
                <i className="fa-solid fa-arrow-right text-xs" />
              </Link>
              <a
                href="tel:28594349"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 border border-white/20 px-8 py-4 text-base font-bold text-white hover:bg-white/20 transition-all whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-[#d92323]" />
                <span className="whitespace-nowrap">Ring til T.H Byg: (+45) 28 59 43 49</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content & Benefits Section */}
      <section className="py-16 sm:py-24 bg-white text-slate-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8 space-y-8">
              <div>
                <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">
                  Et ståltag er den moderne tagløsning
                </h2>
                <p className="mt-4 text-slate-700 leading-relaxed text-base">
                  Søger du efter dit nye ståltag nær Vejle, Horsens og Hedensted?
                </p>
                <p className="mt-3 text-slate-700 leading-relaxed">
                  Hos T-H Byg har vi stor erfaring med lægning af holdbare og smukke ståltage på boliger, sommerhuse og virksomheder. Vi laver arbejde for vores kunder med ståltag i Hedensted, Vejle, Horsens og omegn. Vi hjælper også gerne dig, hvis du ønsker et tag, der er holdbart, overkommeligt i pris og smukt at se på. Få lavet et økonomisk fornuftigt tag af enten decra eller metrotile.
                </p>
              </div>

              {/* Benefits list verbatim from scrape */}
              <div className="bg-[#f4f5f7] p-8 rounded-2xl border border-slate-200 space-y-6">
                <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                  <i className="fa-solid fa-shield-check text-[#d92323]" />
                  <span>Fordele ved et ståltag</span>
                </h3>

                <div className="space-y-4 text-slate-700 text-sm leading-relaxed">
                  <div className="bg-white p-5 rounded-xl border border-slate-200">
                    <h4 className="font-bold text-slate-900 text-base mb-1">Økonomisk fordel</h4>
                    <p>Et af de mest tiltalende aspekter ved ståltag er den betydelige økonomiske besparelse i forhold til andre tagmaterialer. Med et ståltag får du en omkostningseffektiv løsning uden at gå på kompromis med kvalitet og holdbarhed.</p>
                  </div>

                  <div className="bg-white p-5 rounded-xl border border-slate-200">
                    <h4 className="font-bold text-slate-900 text-base mb-1">Direkte installation</h4>
                    <p>Ståltage kan monteres direkte på det eksisterende undertag. Hvilket betyder, at du ikke nødvendigvis behøver at foretage omfattende ændringer vedrørende tagkonstruktionen. Denne direkte installation sparer både tid og arbejdskraft og kan resultere i en hurtigere og mere smidig proces.</p>
                  </div>

                  <div className="bg-white p-5 rounded-xl border border-slate-200">
                    <h4 className="font-bold text-slate-900 text-base mb-1">Fleksibel på forskellige tagkonstruktioner</h4>
                    <p>Ståltage er kendt for deres lette vægt i forhold til nogle andre tagmaterialer. Dette gør det muligt at lægge ståltage på tagkonstruktioner, der måske ikke er i stand til at bære tyngden af tungere tagmaterialer som tegl eller skifer. Denne alsidighed åbner op for flere designmuligheder og gør ståltage til et levedygtigt valg for en bred vifte af bygningstyper.</p>
                  </div>

                  <div className="bg-white p-5 rounded-xl border border-slate-200">
                    <h4 className="font-bold text-slate-900 text-base mb-1">Farvenuancer og æstetik</h4>
                    <p>Ståltage er tilgængelige i en bred vifte af farvenuancer og stilarter, hvilket gør det muligt at skræddersy tagets udseende efter dine præferencer. Dette giver dig mulighed for at opnå den ønskede æstetik, der komplementerer din ejendoms stil og karakter.</p>
                  </div>

                  <div className="bg-white p-5 rounded-xl border border-slate-200">
                    <h4 className="font-bold text-slate-900 text-base mb-1">Minimal vedligeholdelse</h4>
                    <p>Ståltage kræver minimal vedligeholdelse i forhold til nogle andre tagmaterialer. Stålets modstandsdygtighed over for vejrforhold og dets holdbare natur betyder, at du ikke behøver at bekymre dig om regelmæssige reparationer og udskiftninger.</p>
                  </div>

                  <div className="bg-white p-5 rounded-xl border border-slate-200">
                    <h4 className="font-bold text-slate-900 text-base mb-1">Langvarig garanti</h4>
                    <p>Et bemærkelsesværdigt træk ved ståltage er den imponerende garantiperiode, der tilbydes. Med op til 40 års garanti kan du være sikker på, at dit tag vil forblive i optimal stand i mange år fremover, hvilket giver dig ro i sindet og værdi for din investering.</p>
                  </div>
                </div>
              </div>

              {/* Metrotile og Decra tag section */}
              <div className="space-y-4 pt-4">
                <h3 className="text-2xl font-bold text-slate-900">
                  Metrotile og Decra tag i Skanderborg, Vejle og Horsens
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  Vil du have et prisbilligt tag, der holder, og et tag, der kan lægges hurtigt og nemt på alle typer bygninger og huse? Så skal du have et Decra tag eller et af de nye Metrotile tage.
                </p>
                <p className="text-slate-700 leading-relaxed">
                  Decra tag har været kendt i mange år, og Metrotile er den nye generation af ståltage fra Decra. De to tage minder meget om hinanden, dog har de nye Metrotiles en lidt længere levetid end et klassisk Decra tag.
                </p>
              </div>

              {/* Applications */}
              <div className="pt-4 border-t border-slate-200">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Løsninger og anvendelser for ståltag</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm font-semibold text-slate-800">
                  <div className="p-3 bg-[#f4f5f7] rounded-lg border border-slate-200 flex items-center gap-2">
                    <i className="fa-solid fa-house text-[#d92323]" /> Parcelhuse
                  </div>
                  <div className="p-3 bg-[#f4f5f7] rounded-lg border border-slate-200 flex items-center gap-2">
                    <i className="fa-solid fa-tree text-[#d92323]" /> Sommerhuse
                  </div>
                  <div className="p-3 bg-[#f4f5f7] rounded-lg border border-slate-200 flex items-center gap-2">
                    <i className="fa-solid fa-warehouse text-[#d92323]" /> Garager & Udhuse
                  </div>
                  <div className="p-3 bg-[#f4f5f7] rounded-lg border border-slate-200 flex items-center gap-2">
                    <i className="fa-solid fa-car text-[#d92323]" /> Carporte
                  </div>
                  <div className="p-3 bg-[#f4f5f7] rounded-lg border border-slate-200 flex items-center gap-2">
                    <i className="fa-solid fa-industry text-[#d92323]" /> Erhvervsbygninger
                  </div>
                  <div className="p-3 bg-[#f4f5f7] rounded-lg border border-slate-200 flex items-center gap-2">
                    <i className="fa-solid fa-hammer text-[#d92323]" /> Eksisterende tage
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar contact box */}
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-[#11161a] text-white p-8 rounded-2xl shadow-xl sticky top-28 space-y-6">
                <h3 className="text-xl font-bold text-white border-b border-slate-800 pb-3">
                  Få tilbud på ståltag
                </h3>
                <p className="text-sm text-slate-300">
                  Ønsker du at modtage yderligere information eller et skriftligt tilbud på ståltag?
                </p>

                <div className="space-y-4 text-sm">
                  <a
                    href="tel:28594349"
                    className="flex items-center gap-3 p-3.5 bg-slate-800/80 rounded-xl hover:bg-[#d92323] transition-colors group"
                  >
                    <i className="fa-solid fa-phone text-[#d92323] group-hover:text-white" />
                    <div>
                      <p className="text-xs text-slate-400 group-hover:text-white/80">Ring direkte til Jacob</p>
                      <p className="font-bold text-white whitespace-nowrap">(+45) 28 59 43 49</p>
                    </div>
                  </a>

                  <a
                    href="mailto:jacob.t.h.byg@gmail.com"
                    className="flex items-center gap-3 p-3.5 bg-slate-800/80 rounded-xl hover:bg-[#d92323] transition-colors group"
                  >
                    <i className="fa-solid fa-envelope text-[#d92323] group-hover:text-white" />
                    <div>
                      <p className="text-xs text-slate-400 group-hover:text-white/80">Send os en e-mail</p>
                      <p className="font-semibold text-white text-xs truncate">jacob.t.h.byg@gmail.com</p>
                    </div>
                  </a>
                </div>

                <div className="pt-2 border-t border-slate-800 text-xs text-slate-400 space-y-1">
                  <p><strong className="text-white">Adresse:</strong> Skovhusvej 23, 8763 Rask Mølle</p>
                  <p><strong className="text-white">CVR:</strong> 31819547</p>
                </div>

                <Link
                  to="/kontakt"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#d92323] py-3.5 text-sm font-bold text-white hover:bg-[#b81b1b] transition-all"
                >
                  Gå til kontaktformular
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DEDICATED STEEL ROOF GALLERY */}
      <section className="py-16 sm:py-24 bg-[#11161a] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl font-extrabold text-white">
              Galleri – Arbejde med Ståltag
            </h2>
            <p className="mt-2 text-slate-400 text-sm">
              Klik på et billede for at åbne det i stor visning
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((img, idx) => (
              <div
                key={idx}
                className="group relative rounded-none overflow-hidden bg-slate-900 border border-slate-800 shadow-xl cursor-pointer"
                onClick={() => {
                  setActiveIndex(idx);
                  setLightboxOpen(true);
                }}
              >
                <img
                  src={img}
                  alt={`Ståltag projekt ${idx + 1}`}
                  className="h-64 w-full object-cover transition-transform duration-500 group-hover:scale-105 rounded-none"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="rounded-none bg-[#d92323] p-3 text-white">
                    <i className="fa-solid fa-magnifying-glass-plus text-lg" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <LightboxModal
        images={galleryImages}
        currentIndex={activeImageIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNavigate={(idx) => setActiveIndex(idx)}
      />
    </div>
  );
}
